export const validateScheduleAppointment = (
  dateOfAppointment: Date | null,
  startTime: string | number | null,
  endTime: string | number | null,
  appointmentType: string | number | null,
  QMEDoctor: string | number | null,
  appointmentCategory: string | number | null,
  ismedicalExam: any,
  specifyNeeds: string,
  isTransportation: boolean,
  isInterpreterReq: boolean | null,
  languageNeed: string | number | null,
  vendor: string | number | null,
  clinicId: string | number | null,
  showAddClinic: boolean,
  addressDetails: any,
  emailAddress: (string | number)[],
  phoneNumber: (string | number)[],
  faxNumber: (string | number)[],
  interpreterDetailsName: string,
  interpreterDetailsPhoneNo: (string | number)[],
  interpreterDetailsCertiNo: string,
  showAddInterpreter: boolean,
  injury: any,
  isManualEntry: boolean,
  manualAddress: string,
  manualLocation: string,
  manualCity: string,
  manualCountry: string,
  manualState: string,
  manualZip: string,
  patientDetailsList?: any,
  isScheduleAppointmentView?: boolean,
  viewScheduleAppointment?: string,
): string[] => {
  const errors: string[] = [];
  const trimmedEmail = emailAddress;
  const trimmedPhone = phoneNumber;
  const trimmedFax = faxNumber;
  
  if (viewScheduleAppointment === "Appointment" && !isScheduleAppointmentView && !patientDetailsList?.caseSubjectId) {
    errors.push("Please Enter Patient Details");
  }
  // Time Normalization Helper
  const normalizeTime = (time: string | number | null): string => {
    if (time == null) return "";
    const timeStr = time.toString().trim();
    if (/^\d{1,2}$/.test(timeStr)) {
      return timeStr.padStart(2, "0") + ":00";
    }
    return timeStr;
  };

  // Normalize Times
  const normalizedStartTime = normalizeTime(startTime);
  const normalizedEndTime = normalizeTime(endTime);
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

  if (!dateOfAppointment) {
    errors.push("Please select Date of Appointment");
  }
  if (!startTime) {
    errors.push("Please select Start Time");
  } else if (!timeRegex.test(normalizedStartTime)) {
    errors.push("Start Time must be in HH:mm format");
  }

  if (!endTime) {
    errors.push("Please select End Time");
  } else if (!timeRegex.test(normalizedEndTime)) {
    errors.push("End Time must be in HH:mm format");
  }

  // Time comparison logic
  if (
    timeRegex.test(normalizedStartTime) &&
    timeRegex.test(normalizedEndTime)
  ) {
    const start = new Date(`2000-01-01T${normalizedStartTime}`).getTime();
    const end = new Date(`2000-01-01T${normalizedEndTime}`).getTime();
    const diffInMinutes = (end - start) / (1000 * 60);
    if (end <= start) {
      errors.push("End Time must be greater than Start Time");
    } else if (
      diffInMinutes < 1 &&
      !(normalizedStartTime === "23:30" && normalizedEndTime === "23:59")
    ) {
      errors.push("Duration must be at least 1 minute.");
    }
  }

  if (dateOfAppointment && timeRegex.test(normalizedStartTime)) {
    const selectedDate = new Date(dateOfAppointment);
    const today = new Date();

    if (selectedDate.toDateString() === today.toDateString()) {
      const currentTime =
        today.getHours().toString().padStart(2, "0") +
        ":" +
        today.getMinutes().toString().padStart(2, "0");

      const now = new Date(`2000-01-01T${currentTime}`).getTime();
      const start = new Date(`2000-01-01T${normalizedStartTime}`).getTime();

      if (start < now) {
        errors.push("Start Time cannot be less than current time");
      }
    }
  }

  if (!appointmentType) {
    errors.push("Please select Exam Type");
  }
  if (!QMEDoctor) {
    errors.push("Please select QME Doctor");
  }

  if (!ismedicalExam) {
    errors.push("Please select Medical Exam");
  }
  if (isInterpreterReq === null) {
    errors.push("Please select whether Interpreter is required");
  }
  if (isInterpreterReq === true && !languageNeed) {
    errors.push("Please select Language Need if Interpreter Required is Yes");
  }

  if (isInterpreterReq) {
    if (!showAddInterpreter && !interpreterDetailsName) {
      errors.push("Please Search Interpreter Name");
    } else {
      if (!interpreterDetailsName) {
        errors.push("Please add Interpreter Name");
      }

      if (
        !interpreterDetailsPhoneNo ||
        interpreterDetailsPhoneNo.length === 0
      ) {
        errors.push("Please add Phone Number from Interpreter details");
      } else {
        if (interpreterDetailsPhoneNo.length > 3) {
          errors.push("Only 3 phone numbers are allowed.");
        } else {
          const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
          const phoneSet = new Set<string>();

          interpreterDetailsPhoneNo.forEach((num) => {
            const trimmed = num.toString().trim();

            if (!phoneRegex.test(trimmed)) {
              errors.push(`Invalid Phone No: ${trimmed}`);
            }

            if (phoneSet.has(trimmed)) {
              errors.push(`Duplicate Phone No: ${trimmed}`);
            } else {
              phoneSet.add(trimmed);
            }
          });
        }
      }

      if (!interpreterDetailsCertiNo) {
        errors.push("Please add Certificate Number");
      }
    }
  }
  if (!injury?.length) {
    errors.push("Please select Injury");
  }
  if (!showAddClinic && !clinicId) {
    errors.push("Please select QME Doctor Associated Location");
  }
  if (
    showAddClinic &&
    addressDetails?.providerAddress?.length == 0 &&
    !isManualEntry
  ) {
    errors.push("Please Search Location");
  }

  if (showAddClinic && isManualEntry && !manualLocation) {
    errors.push("Please add Manual Location");
  }
  if (showAddClinic && isManualEntry && !manualAddress) {
    errors.push("Please add Manual Address Line 1");
  }
  if (showAddClinic && isManualEntry && !manualCity) {
    errors.push("Please add Manual City");
  }
  if (showAddClinic && isManualEntry && !manualState) {
    errors.push("Please add Manual State");
  }
  if (showAddClinic && isManualEntry && !manualCountry) {
    errors.push("Please add Manual Country");
  }
  if (showAddClinic && isManualEntry && !manualZip) {
    errors.push("Please add Manual Zip Code");
  }

  // Validate Phone Number
  if (emailAddress?.length > 0) {
    const filteredEmails = emailAddress.filter(
      (e): e is string | number => !!e?.toString().trim()
    );

    const totalChars = filteredEmails.reduce((acc: number, email) => {
      const strEmail = String(email).trim();
      return acc + strEmail.length;
    }, 0);

    if (totalChars > 80) {
      errors.push(`Total length of all email addresses are too long.`);
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      const emailSet = new Set();
      emailAddress.forEach((email) => {
        const trimmedEmailAddress = email.toString().trim();

        if (!emailRegex.test(trimmedEmailAddress)) {
          errors.push(`Invalid Email Address: ${trimmedEmailAddress}`);
        }

        if (emailSet.has(trimmedEmailAddress)) {
          errors.push(`Duplicate Email Address: ${trimmedEmailAddress}`);
        } else {
          emailSet.add(trimmedEmailAddress);
        }
      });
    }
  }

  if (phoneNumber?.length > 0) {
    if (phoneNumber?.length > 3) {
      errors.push("Only 3 phone numbers are allowed.");
    } else {
      const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;

      const phoneSet = new Set();
      phoneNumber.forEach((num) => {
        const trimmedPhoneNumber = num.toString().trim();

        if (!phoneRegex.test(trimmedPhoneNumber)) {
          errors.push(`Invalid Phone No: ${trimmedPhoneNumber}`);
        }

        if (phoneSet.has(trimmedPhoneNumber)) {
          errors.push(`Duplicate Phone No: ${trimmedPhoneNumber}`);
        } else {
          phoneSet.add(trimmedPhoneNumber);
        }
      });
    }
  }

  // Validate Fax Number
  if (faxNumber?.length > 0) {
    if (phoneNumber?.length > 3) {
      errors.push("Only 3 fax numbers are allowed.");
    } else {
      const faxRegex = /^\d{3}-\d{3}-\d{4}$/;

      const faxSet = new Set();
      faxNumber.forEach((num) => {
        const trimmedFaxNumber = num.toString().trim();

        if (!faxRegex.test(trimmedFaxNumber)) {
          errors.push(`Invalid Fax No: ${trimmedFaxNumber}`);
        }

        if (faxSet.has(trimmedFaxNumber)) {
          errors.push(`Duplicate Fax No: ${trimmedFaxNumber}`);
        } else {
          faxSet.add(trimmedFaxNumber);
        }
      });
    }
  }

  return errors;
};
